/**
 * The Rooms menu: the visitor's blueprints — one room each — to switch
 * between, rename, delete, or add to. The 3D view shows the active one only.
 */

import React, { useState } from 'react';
import { Check, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { DesignerMenu, DesignerMenuDivider, DesignerMenuItem } from '../chrome/DesignerMenu';
import { DESIGNER_STRINGS as S } from '../strings';

export interface BlueprintSummary {
  id: string;
  name: string;
  /** Whether it has its room yet. */
  hasRoom: boolean;
}

export interface BlueprintsMenuProps {
  blueprints: BlueprintSummary[];
  activeId: string;
  onSwitch: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, name: string) => void;
  /** Asks first; the page confirms. */
  onDelete: (id: string) => void;
}

const smallButton: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0,
  color: '#cfcfd6', background: 'none', border: 'none', borderRadius: 5, cursor: 'pointer', flex: '0 0 auto',
};

export const BlueprintsMenu: React.FC<BlueprintsMenuProps> = ({ blueprints, activeId, onSwitch, onNew, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const R = S.rooms;

  const commitRename = () => {
    if (!renaming) return;
    const name = renaming.value.trim();
    if (name) onRename(renaming.id, name);
    setRenaming(null);
  };

  return (
    <DesignerMenu icon={<Layers size={14} />} label={R.title} title={R.menuTitle} minWidth={220} onOpenChange={(open) => { if (!open) setRenaming(null); }}>
      {(close) => (
        <>
          {blueprints.map((bp) => {
            const active = bp.id === activeId;
            if (renaming?.id === bp.id) {
              return (
                <div key={bp.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 4px 3px 8px' }}>
                  <input
                    type="text" value={renaming.value} maxLength={40} autoFocus aria-label={R.rename}
                    onChange={(e) => setRenaming({ id: bp.id, value: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitRename(); } }}
                    onBlur={commitRename}
                    style={{ flex: '1 1 auto', minWidth: 0, padding: '4px 6px', fontSize: 12, borderRadius: 5, color: '#fff', background: 'rgba(255,255,255,0.1)', border: '1px solid #FEB100' }}
                  />
                  <button type="button" style={smallButton} title={R.renameDone} aria-label={R.renameDone} onMouseDown={(e) => e.preventDefault()} onClick={commitRename}><Check size={14} /></button>
                </div>
              );
            }
            return (
              <DesignerMenuItem
                key={bp.id}
                label={bp.hasRoom ? bp.name : `${bp.name} · ${R.empty}`}
                active={active}
                onClick={() => { close(); if (!active) onSwitch(bp.id); }}
                trailing={(
                  <span style={{ display: 'flex', gap: 2, marginLeft: 6 }} onClick={(e) => e.stopPropagation()}>
                    <span role="button" tabIndex={0} style={smallButton} title={R.rename} aria-label={R.rename}
                      onClick={() => setRenaming({ id: bp.id, value: bp.name })}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRenaming({ id: bp.id, value: bp.name }); } }}
                    ><Pencil size={13} /></span>
                    <span role="button" tabIndex={0} style={{ ...smallButton, color: '#ffb4ab' }} title={R.delete} aria-label={R.delete}
                      onClick={() => { close(); onDelete(bp.id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); close(); onDelete(bp.id); } }}
                    ><Trash2 size={13} /></span>
                  </span>
                )}
              />
            );
          })}
          <DesignerMenuDivider />
          <DesignerMenuItem icon={<Plus size={14} />} label={R.newRoom} onClick={() => { close(); onNew(); }} />
        </>
      )}
    </DesignerMenu>
  );
};

export default BlueprintsMenu;
